import { computed, inject, Signal, OnInit, Directive } from '@angular/core';
import { GuardianshipOverviewStateService } from '../../../../shared/services/guardianship-overview-state.service';
import { CaseContextService } from '@app/core/services/case-context/case-context.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import { StorageService } from '@shared/services/storage.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { GuardianshipOverview } from '../../../../shared/interfaces/guardianship-overview.interface';
import { SubscriberStateService } from '@app/guardianship/features/dashboard/shared/services/subscriber-state.service';
import { SubscribersHttpService } from '@app/guardianship/features/dashboard/features/subscribers/shared/services/subscriber-http.service';
import { tap } from 'rxjs';
import { getIsClerk } from '@shared/utils';

@Directive({
  selector: '[appGuardianshipOverviewSetter]',
})
export class GetOverviewAndSetSubscriberIdDirective implements OnInit {
  private readonly _stateService = inject(GuardianshipOverviewStateService);
  private readonly _caseContextService = inject(CaseContextService);
  private readonly _storageService = inject(StorageService);
  private readonly _subscriberHttpService = inject(SubscribersHttpService);
  private readonly _subscriberState = inject(SubscriberStateService);
  private readonly _isClerk = getIsClerk();

  private readonly _route = inject(ActivatedRoute);
  private readonly _data = toSignal(this._route.data);
  private readonly _overview: Signal<GuardianshipOverview | null> = computed(
    () => {
      const data = this._data();
      return data ? data['guardianshipOverview'] : null;
    },
  );

  ngOnInit() {
    const overview = this._overview();
    if (!overview) {
      console.error('No overview data found in route resolver');
      return;
    }

    this._setOverview(overview);

    const subscriberId: string | null = overview.subscriberId;

    this._subscriberHttpService
      .getSubscribers(overview.id)
      .pipe(
        tap((subscribers) => {
          const firstSubscriberId = subscribers[0].id;
          if (this._isClerk()) {
            this._subscriberState.setSelectedSubscriberId(firstSubscriberId);
            return;
          }

          const loggedSubscriberId = Number(subscriberId ?? firstSubscriberId);
          this._subscriberState.setSelectedSubscriberId(loggedSubscriberId);
        }),
      )
      .subscribe();
  }

  private _setOverview(overview: GuardianshipOverview) {
    this._stateService.setOverview(overview);
    this._storageService.save(StorageKey.guardianshipOverview, overview);
    this._caseContextService.setContext({
      id: overview.id,
      type: overview.applicationType,
      courtId: overview.courtId,
      roles: overview.roles,
      uuid: overview.uuid.toString(),
      subscriberId: Number(overview.subscriberId),
    });
  }
}
